import React, { useState, useRef, useEffect, memo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import '../styles/LanguageSwitcher.css';

const dropdownVariants = {
  hidden: { 
    opacity: 0, 
    y: 8,
    scale: 0.95,
  },
  visible: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    transition: {
      duration: 0.2,
      ease: [0.22, 1, 0.36, 1],
    },
  },
  exit: { 
    opacity: 0, 
    y: 8,
    scale: 0.95,
    transition: {
      duration: 0.15,
    },
  },
};

const LanguageSwitcher = () => {
  const { language, changeLanguage, languages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLang = languages.find(l => l.code === language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = useCallback((code) => {
    changeLanguage(code);
    setIsOpen(false);
  }, [changeLanguage]);

  const toggleDropdown = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  return (
    <motion.div 
      className="language-switcher" 
      ref={dropdownRef}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        duration: 0.4, 
        delay: 0.2,
        ease: [0.22, 1, 0.36, 1],
      }}
    >
      <motion.button
        className="language-button"
        onClick={toggleDropdown}
        aria-label="Change language"
        aria-expanded={isOpen}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <span className="language-flag">{currentLang.flag}</span>
      </motion.button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            className="language-dropdown"
            variants={dropdownVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {languages.map((lang) => (
              <motion.button
                key={lang.code}
                className={`language-option ${lang.code === language ? 'active' : ''}`}
                onClick={() => handleSelect(lang.code)}
                whileHover={{ backgroundColor: 'rgba(237, 228, 211, 0.06)' }}
              >
                <span className="language-flag">{lang.flag}</span>
                <span className="language-name">{lang.name}</span>
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default memo(LanguageSwitcher);
